import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "disabled", "id", "inline", "label", "alignCheckboxTop", "dangerouslyForceFocusStyling", "dangerouslyForceHoverStyling"];
import React from 'react';
import { and } from 'airbnb-prop-types';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { useDeprecatedClassNameStylePropsWarning } from '../../internal/deprecated-classname-style-props';
import { ActionBlock } from '../ActionBlock/ActionBlock';
import styles from './Checkbox.module.less';
/**
 * Custom prop type validator that checks string length.
 */

var maxLineLength = {};

maxLineLength.isRequired = function (props, propName) {
  var componentName = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'ANONYMOUS';

  if (props[propName]) {
    var value = props[propName];
    var maxChars = 70;

    if (typeof value === 'string') {
      return value.length <= maxChars ? null : new Error("Keep ".concat(propName, " in ").concat(componentName, " under ").concat(maxChars, " characters. Line length on ").concat(propName, " is ").concat(value.length - maxChars, " characters too long."));
    }
  }

  return null;
};

var BlockLabel = function BlockLabel(_ref) {
  var children = _ref.children,
      className = _ref.className,
      disabled = _ref.disabled,
      dangerouslyForceFocusStyling = _ref.dangerouslyForceFocusStyling,
      dangerouslyForceHoverStyling = _ref.dangerouslyForceHoverStyling,
      alignCheckboxTop = _ref.alignCheckboxTop;
  return /*#__PURE__*/React.createElement(ActionBlock, {
    dangerouslyForceHoverStyling: dangerouslyForceHoverStyling,
    dangerouslyForceFocusStyling: dangerouslyForceFocusStyling,
    component: "span",
    mode: "group",
    disabled: disabled,
    className: classNames([className, styles.label, styles['label--block'],
    /**
     * When the label of a checkbox spans multiple lines, it can
     * be a desirable product use case to top-align the checked indicator.
     * This style is opted into with the alignCheckboxTop prop.
     */
    classes({
      'align-items-flex-start': alignCheckboxTop,
      'align-items-center': !alignCheckboxTop
    })])
  }, children);
}; // eslint-disable-next-line react/prop-types


var InlineLabel = function InlineLabel(_ref2) {
  var className = _ref2.className,
      children = _ref2.children;
  return /*#__PURE__*/React.createElement("span", {
    className: classNames([className, styles.label, styles['label--inline']])
  }, children);
};
/**
 * The points in this line were derived geometrically from the outline of the
 * checkbox provided by design; they're not just magic numbers. We can't just
 * use the SVG asset directly because we need a single line to animate.
 */


var CheckPolyline = function CheckPolyline() {
  return /*#__PURE__*/React.createElement("polyline", {
    className: styles.check,
    points: "4.845,11.855 10.015,17.025 20.155,6.885"
  });
};
/**
 * A single checkbox option with label. Can be rendered in a block or inline.
 * Note that focusable="false" for the SVG below is necessary to avoid being tabbable on IE
 */


export var CheckboxOption = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var className = props.className,
      disabled = props.disabled,
      id = props.id,
      inline = props.inline,
      label = props.label,
      alignCheckboxTop = props.alignCheckboxTop,
      dangerouslyForceFocusStyling = props.dangerouslyForceFocusStyling,
      dangerouslyForceHoverStyling = props.dangerouslyForceHoverStyling,
      otherProps = _objectWithoutProperties(props, _excluded);

  useDeprecatedPropsWarning({
    component: 'CheckboxOption',
    deprecatedProps: [{
      version: '10.5.0',
      deprecated: 'inline',
      migration: 'Inline checkbox options are no longer recommended for use in designs.'
    }],
    props: {
      inline: inline
    }
  });
  useDeprecatedClassNameStylePropsWarning('CheckboxOption', {
    className: className,
    style: otherProps.style
  });
  var LabelComponent = inline ? InlineLabel : BlockLabel;
  return /*#__PURE__*/React.createElement("label", {
    htmlFor: id,
    ref: ref
  }, /*#__PURE__*/React.createElement("input", _extends({
    type: "checkbox",
    disabled: disabled,
    className: styles.input,
    id: id
  }, otherProps)), /*#__PURE__*/React.createElement(LabelComponent, {
    className: className,
    disabled: disabled,
    alignCheckboxTop: alignCheckboxTop
    /** Dangerous props used for testing. */
    ,
    dangerouslyForceFocusStyling: dangerouslyForceFocusStyling,
    dangerouslyForceHoverStyling: dangerouslyForceHoverStyling
  }, /*#__PURE__*/React.createElement("span", {
    className: styles['checkbox-wrapper']
  }, /*#__PURE__*/React.createElement("span", {
    className: styles['checkbox-back']
  }), /*#__PURE__*/React.createElement("svg", {
    focusable: "false",
    className: styles['checkbox-front'],
    xmlns: "http://www.w3.org/2000/svg",
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    version: "1.1"
  }, /*#__PURE__*/React.createElement(CheckPolyline, null))), label));
});
CheckboxOption.displayName = 'CheckboxOption';

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require
  var PropTypes = require('prop-types');

  CheckboxOption.propTypes = {
    /** @deprecated */
    className: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string,
    inline: PropTypes.bool,
    label: and([maxLineLength, PropTypes.node]).isRequired,

    /** Boolean flag to top-align the checkbox when the checkbox label spans multiple lines. */
    alignCheckboxTop: PropTypes.bool,

    /** Force the component to use its focus styles. */
    dangerouslyForceFocusStyling: PropTypes.bool,

    /** Force the component to use its hover styles. */
    dangerouslyForceHoverStyling: PropTypes.bool
  };
}

CheckboxOption.defaultProps = {
  className: null,
  disabled: false,
  id: null,
  inline: false,
  alignCheckboxTop: false,
  dangerouslyForceFocusStyling: false,
  dangerouslyForceHoverStyling: false
};